import { useState, type FormEvent } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { forgotPassword } from '@/api/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Shield, Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const isLoading = useAuthStore((s) => s.isLoading);
  const error = useAuthStore((s) => s.error);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);

  // Redirect if already logged in
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password);
      navigate('/dashboard', { replace: true });
    } catch {
      // Error is handled in store
    }
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setIsSendingReset(true);
    try {
      await forgotPassword(forgotEmail);
      toast.success('비밀번호 재설정 이메일이 전송되었습니다.', { description: '이메일을 확인해주세요.' });
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch {
      toast.error('비밀번호 재설정 이메일 전송에 실패했습니다.');
    } finally {
      setIsSendingReset(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-5 px-4">
      <Card className="w-full max-w-md bg-background border-gray-20 p-8 shadow-lg">
        {/* Logo / Title */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl text-gray-90 mb-2">AI CCTV 안전관리</h1>
          <p className="text-gray-60 text-sm">건설현장 안전관리 대시보드</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="p-3 bg-danger/10 border border-danger/30 rounded-lg">
              <p className="text-danger text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-80 text-sm">이메일</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              required
              autoComplete="email"
              className="bg-background border-gray-30 focus:border-primary"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-80 text-sm">비밀번호</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호를 입력하세요"
                required
                autoComplete="current-password"
                className="bg-background border-gray-30 focus:border-primary pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-50 hover:text-gray-70"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={isLoading || !email || !password}
            className="w-full bg-primary hover:bg-primary-hover text-white"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                로그인 중...
              </>
            ) : (
              '로그인'
            )}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-primary text-sm hover:underline"
            >
              비밀번호를 잊으셨나요?
            </button>
          </div>
        </form>

        {/* Forgot Password Form */}
        {showForgotPassword && (
          <div className="mt-6 pt-6 border-t border-gray-20">
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="text-gray-60 hover:text-gray-90"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <h2 className="text-gray-90 text-sm font-medium">비밀번호 찾기</h2>
              </div>
              <p className="text-gray-60 text-xs">
                가입한 이메일 주소를 입력하시면 비밀번호 재설정 링크를 보내드립니다.
              </p>
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-gray-80 text-sm">이메일</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="admin@example.com"
                  required
                  className="bg-background border-gray-30 focus:border-primary"
                />
              </div>
              <Button
                type="submit"
                disabled={isSendingReset || !forgotEmail}
                className="w-full bg-primary hover:bg-primary-hover text-white"
              >
                {isSendingReset ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    전송 중...
                  </>
                ) : (
                  '재설정 링크 전송'
                )}
              </Button>
            </form>
          </div>
        )}

        <p className="text-center text-gray-50 text-xs mt-6">
          &copy; 2025 ONTOH. All rights reserved.
        </p>
      </Card>
    </div>
  );
}
